/**
 * Table PDF download: DOM cell serialization and per-table "Download table as PDF" link mounting for Cortex DownloadTablePdf.
 */

function escapeMarkdownLabel(label: string): string {
  return label.replace(/\\/g, "\\\\").replace(/\]/g, "\\]");
}

function resolveHref(anchor: HTMLAnchorElement): string {
  let href = anchor.getAttribute("href") || "";
  if (!href) return "";
  if (/^https?:\/\//i.test(href) || href.startsWith("mailto:")) {
    return href;
  }
  if (href.startsWith("//")) {
    return `${window.location.protocol}${href}`;
  }
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return href;
  }
}

export function serializeTableCellForPdfDownload(cell: HTMLElement): string {
  const parts: string[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.tagName === "A") {
      const a = el as HTMLAnchorElement;
      const href = resolveHref(a);
      const label = (a.textContent || "").trim() || href;
      if (href) {
        parts.push(`[${escapeMarkdownLabel(label)}](${href})`);
      } else {
        parts.push(label);
      }
      return;
    }
    el.childNodes.forEach((child) => walk(child));
  };

  walk(cell);
  return parts.join("").trim();
}

/**
 * Intro text immediately above a rendered markdown table: consecutive <p> siblings before the table,
 * or before the table's parent when the table sits in a wrapper (e.g. div) after those paragraphs.
 */
export function getPdfSubtitleBeforeTable(table: HTMLTableElement): string {
  const paragraphsBefore = (anchor: Element): string[] => {
    const layer: string[] = [];
    let el: Element | null = anchor.previousElementSibling;
    while (el && el.tagName === "P") {
      const t = (el.textContent || "").trim();
      if (t) {
        layer.push(t);
      }
      el = el.previousElementSibling;
    }
    return layer.reverse();
  };

  const direct = paragraphsBefore(table);
  if (direct.length) {
    return direct.join("\n\n");
  }

  const parent = table.parentElement;
  if (parent && parent.tagName !== "BODY") {
    const fromParent = paragraphsBefore(parent);
    if (fromParent.length) {
      return fromParent.join("\n\n");
    }
  }

  return "";
}

const EXPORT_RESULTS_PARAMS_FRAGMENT = "/export_results/?params=";

/** Visible label on the per-table pseudo-link under rendered markdown tables. */
export const TABLE_PDF_LINK_LABEL = "Download table as PDF";

/** Default document title at the top of generated PDFs when none is sent. */
export const TABLE_PDF_DEFAULT_DOCUMENT_TITLE = "Theo Download";

const TABLE_PDF_LOADING_LABEL = "Preparing PDF…";
const TABLE_PDF_MIN_DATA_ROWS = 10;

/**
 * Toggle loading copy on the injected table-PDF anchor (disables click while waiting).
 * Uses data-neo-table-pdf-prev-label to restore the prior label after the request finishes.
 */
export function setTablePdfLinkLoadingState(
  root: HTMLElement | null,
  tableIndex: number,
  loading: boolean
): void {
  const anchor = root?.querySelector(
    `a[data-neo-table-pdf-download="${tableIndex}"]`
  ) as HTMLAnchorElement | null;
  if (!anchor) return;
  if (loading) {
    if (!anchor.dataset.neoTablePdfPrevLabel) {
      anchor.dataset.neoTablePdfPrevLabel = anchor.textContent || TABLE_PDF_LINK_LABEL;
    }
    anchor.textContent = TABLE_PDF_LOADING_LABEL;
    anchor.style.pointerEvents = "none";
    anchor.setAttribute("aria-busy", "true");
  } else {
    anchor.textContent = anchor.dataset.neoTablePdfPrevLabel || TABLE_PDF_LINK_LABEL;
    delete anchor.dataset.neoTablePdfPrevLabel;
    anchor.style.pointerEvents = "";
    anchor.removeAttribute("aria-busy");
  }
}

/** When true, do not show per-table PDF download links (message is an export-results payload). */
export function messageBlocksTablePdfDownloads(rawMessageContent: string): boolean {
  return rawMessageContent.includes(EXPORT_RESULTS_PARAMS_FRAGMENT);
}

/**
 * Inserts a "Download table as PDF" paragraph after each table with >= 10 data rows.
 * Returns cleanup to remove injected nodes.
 */
export function mountTablePdfDownloadLinksBelowTables(root: HTMLElement): () => void {
  root.querySelectorAll('p[data-neo-table-pdf-slot="1"]').forEach((el) => el.remove());
  const created: HTMLElement[] = [];
  const tables = Array.from(root.querySelectorAll("table")) as HTMLTableElement[];
  tables.forEach((table, tableIndex) => {
    const dataRowCount = (Array.from(table.querySelectorAll("tr")) as HTMLTableRowElement[]).filter(
      (row) => row.querySelectorAll("td").length > 0
    ).length;
    if (dataRowCount < TABLE_PDF_MIN_DATA_ROWS) return;
    const p = document.createElement("p");
    p.setAttribute("data-neo-table-pdf-slot", "1");
    p.style.marginTop = "0.35em";
    p.style.marginBottom = "0.65em";
    const a = document.createElement("a");
    a.href = "#";
    a.setAttribute("data-neo-table-pdf-download", String(tableIndex));
    a.textContent = TABLE_PDF_LINK_LABEL;
    Object.assign(a.style, {
      color: "#2563EB",
      textDecoration: "underline",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: "inherit",
    });
    p.appendChild(a);
    table.insertAdjacentElement("afterend", p);
    created.push(p);
  });
  return () => {
    created.forEach((el) => el.remove());
  };
}
